import * as Sentry from '@sentry/tanstackstart-react';
import { createIsomorphicFn } from '@tanstack/react-start';
import { createClient } from 'graphql-sse';
import {
  Environment,
  Network,
  Observable,
  RecordSource,
  Store,
  type GraphQLResponse,
  type RequestParameters,
  type Variables,
} from 'relay-runtime';
import { z } from 'zod';

import { getTokenFn } from '@/auth/common';
import { getStableId } from '@/statsig';

const graphQlResponseSchema = z.object({
  data: z.record(z.string(), z.unknown()).nullish(),
  errors: z
    .array(
      z.object({
        message: z.string(),
        locations: z
          .array(z.object({ line: z.number(), column: z.number() }))
          .optional(),
        path: z.array(z.union([z.string(), z.number()])).optional(),
        severity: z.enum(['CRITICAL', 'ERROR', 'WARNING']).optional(),
      }),
    )
    .optional(),
  extensions: z.record(z.string(), z.unknown()).optional(),
});

const getGraphQlUrls = () => {
  const clientUrl = import.meta.env.VITE_GRAPHQL_API_URL_CLIENT;
  const serverUrl = import.meta.env.VITE_GRAPHQL_API_URL_SERVER;

  if (clientUrl && serverUrl) {
    return { clientUrl, serverUrl };
  }
  const error = new Error('GraphQL URLs are not configured');
  Sentry.captureException(error);
  throw error;
};

const graphQlUrls = getGraphQlUrls();

const createNetwork = (url: string, isServer: boolean) => {
  const isSingleConnection = !isServer;
  const client = createClient({
    url: isSingleConnection ? `${url}/stream` : url,
    singleConnection: isSingleConnection,
    lazy: isSingleConnection,
    lazyCloseTimeout: 30_000,
    retryAttempts: 0,
    credentials: 'include',
    headers: async () => {
      const [token, stableId] = await Promise.all([
        getTokenFn(),
        getStableId(),
      ]);

      return {
        'Content-Type': 'application/json',
        'X-Stable-Id': stableId,
        ...(token ? { Authorization: `Bearer ${token.token}` } : {}),
      };
    },
  });

  const fetchFn = (request: RequestParameters, variables: Variables) =>
    Observable.create<GraphQLResponse>((sink) => {
      if (!request.text) {
        throw new Error('GraphQL query text is required');
      }

      return client.subscribe(
        {
          query: request.text,
          operationName: request.name,
          variables,
        },
        {
          next: (rawPayload) => {
            const payload = graphQlResponseSchema.safeParse(rawPayload);
            if (!payload.success) {
              sink.error(new Error('Invalid GraphQL response'));
              return;
            }
            if (payload.data.data != null) {
              sink.next({
                data: payload.data.data,
                errors: payload.data.errors,
                extensions: payload.data.extensions,
              });
              return;
            }
            if (payload.data.errors != null) {
              sink.next({
                errors: payload.data.errors,
                extensions: payload.data.extensions,
              });
              return;
            }
            sink.error(
              new Error('GraphQL response contained neither data nor errors'),
            );
          },
          error: (error) => {
            sink.error(
              error instanceof Error ? error : new Error(String(error)),
            );
          },
          complete: () => sink.complete(),
        },
      );
    });

  return Network.create(fetchFn, fetchFn);
};

const createEnvironment = (url: string, isServer: boolean) => ({
  environment: new Environment({
    network: createNetwork(url, isServer),
    store: new Store(new RecordSource()),
    isServer,
  }),
});

export const getRelayEnvironment = createIsomorphicFn()
  .server(() => createEnvironment(graphQlUrls.serverUrl, true))
  .client(() => createEnvironment(graphQlUrls.clientUrl, false));
